import { useCallback, useEffect, useMemo, useState } from "react";
import { FileScan, RefreshCw, Search } from "lucide-react";
import { Button, EmptyState, ErrorBanner, LoadingState, PageHeader, StatusBadge } from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import { goodsReceipts, warehouseName } from "../lib/demoData";
import { requireSupabase } from "../lib/supabase";
import type { WarehouseCode } from "../types";
import { OrderLink } from "../components/OrderLink";
import { receiptStatusPresentation } from "../lib/warehouseData";
import { useDeveloperSettings } from "../lib/developerSettings";

type ReceiptStatus = "draft" | "stored" | "partially_picked_up" | "picked_up";

type ReceiptView = {
  id: string;
  orderNumber: string;
  supplier: string;
  department: string;
  warehouse: WarehouseCode;
  warehouseName: string;
  location: string;
  packageCount: number;
  stockedPackages: number;
  receivedAt: string;
  deliveryNoteNumber: string | null;
  hasDocument: boolean;
  status: ReceiptStatus;
  returnStatus: "registered" | "picked_up" | null;
};

const demoRows: ReceiptView[] = goodsReceipts.map((receipt) => ({
  id: receipt.id,
  orderNumber: receipt.orderNumber,
  supplier: receipt.supplier,
  department: receipt.department,
  warehouse: receipt.warehouse,
  warehouseName: warehouseName(receipt.warehouse),
  location: receipt.location,
  packageCount: receipt.packagesTotal,
  stockedPackages: receipt.packagesStored,
  receivedAt: receipt.receivedAt,
  deliveryNoteNumber: null,
  hasDocument: true,
  status: receipt.packagesStored === 0 ? "picked_up" : "stored",
  returnStatus: null
}));

function formatReceivedAt(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "short",
    timeStyle: "short"
  }).format(date);
}

export function GoodsReceiptsPage() {
  const { cloudConfigured } = useAuth();
  const { exampleTexts } = useDeveloperSettings();
  const [receipts, setReceipts] = useState<ReceiptView[]>(cloudConfigured ? [] : demoRows);
  const [loading, setLoading] = useState(cloudConfigured);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState<"all" | WarehouseCode>("all");

  const load = useCallback(async () => {
    if (!cloudConfigured) return;
    setLoading(true);
    const client = requireSupabase();
    const [receiptResult, warehouseResult, orderResult, supplierResult, locationResult, departmentResult, packageResult, documentResult, returnResult] = await Promise.all([
      client.from("goods_receipts").select("id,warehouse_id,work_order_id,supplier_id,storage_location_id,received_at,delivery_note_number,package_count,status").order("received_at", { ascending: false }),
      client.from("warehouses").select("id,code,name"),
      client.from("work_orders").select("id,order_number,department_id"),
      client.from("suppliers").select("id,name"),
      client.from("storage_locations").select("id,warehouse_id,code"),
      client.from("departments").select("id,name"),
      client.from("packages").select("id,goods_receipt_id,status,order_return_id"),
      client.from("delivery_note_document_receipts").select("document_id,goods_receipt_id"),
      client.from("order_returns").select("id,work_order_id,warehouse_id,status,registered_at").order("registered_at", { ascending: false })
    ]);
    const firstError = receiptResult.error
      || warehouseResult.error
      || orderResult.error
      || supplierResult.error
      || locationResult.error
      || departmentResult.error
      || packageResult.error
      || documentResult.error
      || returnResult.error;

    if (firstError) {
      setError(`Wareneingänge konnten nicht geladen werden: ${firstError.message}`);
      setLoading(false);
      return;
    }

    const warehouses = new Map((warehouseResult.data ?? []).map((row) => [row.id, row]));
    const orders = new Map((orderResult.data ?? []).map((row) => [row.id, row]));
    const suppliers = new Map((supplierResult.data ?? []).map((row) => [row.id, row]));
    const locations = new Map((locationResult.data ?? []).map((row) => [row.id, row]));
    const departments = new Map((departmentResult.data ?? []).map((row) => [row.id, row]));
    const packageRows = packageResult.data ?? [];
    const returnRows = returnResult.data ?? [];
    const documentReceiptIds = new Set((documentResult.data ?? []).map((row) => row.goods_receipt_id));

    const normalized: ReceiptView[] = (receiptResult.data ?? []).map((row) => {
      const warehouse = warehouses.get(row.warehouse_id);
      const order = orders.get(row.work_order_id);
      const supplier = suppliers.get(row.supplier_id);
      const location = locations.get(row.storage_location_id);
      const department = order?.department_id ? departments.get(order.department_id) : null;
      const receiptPackages = packageRows.filter((item) => item.goods_receipt_id === row.id);
      const matchingReturns = returnRows.filter((item) => item.work_order_id === row.work_order_id && item.warehouse_id === row.warehouse_id);
      const activeReturn = receiptPackages.some((item) => item.status === "stocked")
        ? matchingReturns.find((item) => item.status === "registered") ?? null
        : null;
      const completedReturn = matchingReturns.find((item) => item.status === "picked_up" && receiptPackages.some((receiptPackage) => receiptPackage.order_return_id === item.id)) ?? null;
      const currentReturn = activeReturn ?? completedReturn;
      return {
        id: row.id,
        orderNumber: order?.order_number ?? "–",
        supplier: supplier?.name ?? "–",
        department: department?.name ?? "Nicht zugeordnet",
        warehouse: (warehouse?.code ?? "MZ") as WarehouseCode,
        warehouseName: warehouse?.name ?? "Unbekanntes Lager",
        location: location?.code ?? "–",
        packageCount: row.package_count,
        stockedPackages: packageRows.filter((item) => item.goods_receipt_id === row.id && item.status === "stocked").length,
        receivedAt: row.received_at,
        deliveryNoteNumber: row.delivery_note_number,
        hasDocument: documentReceiptIds.has(row.id),
        status: row.status as ReceiptStatus,
        returnStatus: (currentReturn?.status as "registered" | "picked_up" | undefined) ?? null
      };
    });
    setReceipts(normalized);
    setError("");
    setLoading(false);
  }, [cloudConfigured]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!cloudConfigured) return;
    const refreshWhenFocused = () => load();
    window.addEventListener("focus", refreshWhenFocused);
    return () => window.removeEventListener("focus", refreshWhenFocused);
  }, [cloudConfigured, load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("de-DE");
    return receipts.filter((receipt) => {
      if (warehouseFilter !== "all" && receipt.warehouse !== warehouseFilter) return false;
      if (!term) return true;
      return [receipt.orderNumber, receipt.supplier, receipt.department, receipt.location, receipt.deliveryNoteNumber ?? ""]
        .some((value) => value.toLocaleLowerCase("de-DE").includes(term));
    });
  }, [receipts, search, warehouseFilter]);

  return (
    <>
      <PageHeader
        title="Wareneingänge"
        description="Wareneingänge aus den Lagergeräten mit aktuellem Cloud-Status."
        action={<Button variant="secondary" onClick={load} disabled={loading}><RefreshCw size={16} /> Aktualisieren</Button>}
      />
      {error && <ErrorBanner message={error} />}
      <div className="toolbar">
        <label className="search-box"><Search size={18} /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={exampleTexts.goodsReceiptsSearch} /></label>
        <select className="input" value={warehouseFilter} onChange={(event) => setWarehouseFilter(event.target.value as "all" | WarehouseCode)}>
          <option value="all">Alle Lager</option>
          <option value="OB">Oberhausen</option>
          <option value="MZ">Merzenich</option>
        </select>
      </div>

      {loading ? <LoadingState label="Wareneingänge werden aus der Testcloud geladen …" /> : filtered.length === 0 ? (
        <EmptyState title="Keine Wareneingänge gefunden" description={receipts.length === 0 ? "Sobald ein Lagergerät einen Entwurf speichert, erscheint er hier." : "Bitte Suche oder Lagerfilter anpassen."} />
      ) : (
        <div className="table-shell">
          <table>
            <thead><tr><th>Auftrag</th><th>Lieferant</th><th>Abteilung</th><th>Lagerplatz</th><th>Packstücke</th><th>Eingang</th><th>Status</th><th>Lieferschein</th></tr></thead>
            <tbody>{filtered.map((receipt) => {
              const presentation = receiptStatusPresentation(receipt);
              return <tr key={receipt.id}>
                <td><strong><OrderLink orderNumber={receipt.orderNumber} /></strong>{receipt.deliveryNoteNumber && <small className="table-subline">LS {receipt.deliveryNoteNumber}</small>}</td>
                <td>{receipt.supplier}</td>
                <td>{receipt.department}</td>
                <td>{receipt.warehouseName} · <strong>{receipt.location}</strong></td>
                <td>{receipt.stockedPackages} / {receipt.packageCount}</td>
                <td>{formatReceivedAt(receipt.receivedAt)}</td>
                <td><StatusBadge tone={presentation.tone}>{presentation.label}</StatusBadge></td>
                <td><StatusBadge tone={receipt.hasDocument ? "success" : "warning"}><FileScan size={13} /> {receipt.hasDocument ? "Erfasst" : "Foto fehlt"}</StatusBadge></td>
              </tr>;
            })}</tbody>
          </table>
        </div>
      )}
    </>
  );
}
